import { useState } from 'react';
import SafeIcon from './SafeIcon';
import './setup.css';
import ConnectSheet from './ConnectSheet';
import type { Account } from '../store/seed';
import type { Page, Role } from '../types';
import type { Language } from '../i18n/locales';
import { SUPPORTED_LOCALES } from '../i18n/locales';
import { useApp, timeLabel } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import type { IconName } from './SafeIcon';

const lockedModules: { label: string; icon: IconName }[] = [
  { label: 'Email marketing', icon: 'FiMail' },
  { label: 'Ads', icon: 'FiTarget' },
  { label: 'Automations', icon: 'FiRepeat' },
];

type IconKey = keyof typeof import('react-icons/fi');

const links: { page: Page; icon: IconKey; key: string }[] = [
  { page: 'calendar', icon: 'FiCalendar', key: 'calendar' },
  { page: 'ideas', icon: 'FiZap', key: 'ideas' },
  { page: 'materials', icon: 'FiImage', key: 'materials' },
  { page: 'brand', icon: 'FiFeather', key: 'brand' },
  { page: 'reports', icon: 'FiBarChart2', key: 'reports' },
  { page: 'allowance', icon: 'FiPieChart', key: 'allowance' },
  { page: 'settings', icon: 'FiSettings', key: 'settings' },
];

const networkIcons: Record<string, string> = {
  Facebook: 'FiFacebook',
  Instagram: 'FiInstagram',
  TikTok: 'FiVideo',
  LinkedIn: 'FiLinkedin',
};

const networkColors: Record<string, string> = {
  Facebook: '#1877F2',
  Instagram: '#E66B91',
  TikTok: '#DDE4F2',
  LinkedIn: '#0A66C2',
};

const SOCIAL_NETWORKS = ['Facebook', 'Instagram', 'TikTok', 'LinkedIn'];
const SCROLL_DELAY_AFTER_PAGE_CHANGE_MS = 100;

function navigationLinksForRole(role: Role) {
  if (role === 'agency')
    return [{ page: 'agencyHome' as Page, icon: 'FiGrid' as IconKey, key: 'agencyHome' }];
  if (role === 'admin')
    return [{ page: 'platformHome' as Page, icon: 'FiShield' as IconKey, key: 'platformHome' }];
  return links;
}

function sectionLabelForRole(role: Role) {
  if (role === 'admin') return 'Platform';
  if (role === 'agency') return 'Agency';
  return 'Workspace';
}

function profileHealthClass(health: string) {
  if (health === 'Connected') return 'healthy';
  if (health === 'Needs reconnecting') return 'unhealthy';
  return 'disconnected';
}

function NetworkIcon({ network }: { network: string }) {
  return (
    <span className="network-icon" style={{ color: networkColors[network] || 'var(--amy-muted)' }}>
      <SafeIcon name={(networkIcons[network] || 'FiGlobe') as never} size={16} />
    </span>
  );
}

export default function Sidebar({
  page,
  setPage,
  role,
  open,
  labels,
  onClose,
  language,
  setLanguage,
}: {
  page: Page;
  setPage: (page: Page) => void;
  role: Role;
  open: boolean;
  labels: Record<string, string>;
  onClose: () => void;
  language: Language;
  setLanguage: (value: Language) => void;
}) {
  const { translate, readOnlyProps, readOnly } = useApp();
  const { route, notify, actor, navigate } = useUi();
  const { commands, state } = useDemo();
  const { accounts: profiles, workspace, notices } = useWorkspaceData(route.workspaceId);
  const [profilesOpen, setProfilesOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState<Account | 'new' | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactText, setContactText] = useState('');
  const myRequests = state.requests.filter((request) => request.workspaceId === workspace.id);
  const [mobileNoticesOpen, setMobileNoticesOpen] = useState(false);
  const visibleLinks = navigationLinksForRole(role);
  const unreadNotices = notices.filter((notice) => !notice.read);
  const isWorkspaceMember = role === 'owner' || role === 'contributor';

  function sendContact() {
    if (!contactText.trim()) return;
    commands.contactAgency(workspace.id, contactText.trim(), actor);
    setContactText('');
    notify(translate('Message sent to Hakusana. They usually reply within one working day.'));
  }

  function manageConnections() {
    setProfilesOpen(false);
    setPage('settings');
    window.setTimeout(
      () =>
        document
          .getElementById('settings-connections')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      SCROLL_DELAY_AFTER_PAGE_CHANGE_MS
    );
  }

  return (
    <>
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-heading">
          <strong>AMY</strong>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close menu')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <div className="sidebar-label">{translate(sectionLabelForRole(role))}</div>
        {visibleLinks.map((item) => (
          <button
            key={item.page}
            className={`nav-item ${page === item.page ? 'active' : ''}`}
            onClick={() => setPage(item.page)}
          >
            <SafeIcon name={item.icon} />
            {labels[item.key] || item.key}
          </button>
        ))}

        {isWorkspaceMember && (
          <>
            <div className="module-divider" />
            <div className="sidebar-label module-label">{translate('Modules')}</div>
            <div className="module-selector-wrap">
              <button
                className={`module-selector ${profilesOpen ? 'expanded' : ''}`}
                aria-expanded={profilesOpen}
                onClick={() => setProfilesOpen((value) => !value)}
              >
                <span className="module-selector-copy">
                  <strong>{translate('Social Media')}</strong>
                  <span className="module-network-icons">
                    {SOCIAL_NETWORKS.map((network) => (
                      <NetworkIcon key={network} network={network} />
                    ))}
                  </span>
                </span>
                <SafeIcon name={profilesOpen ? 'FiChevronUp' : 'FiChevronDown'} size={16} />
              </button>
              {profilesOpen && (
                <div className="profiles-dropdown">
                  <h3>{translate('Connected profiles')}</h3>
                  {profiles.map((profile) => {
                    const isMyProfile = profile.owner === actor.name;
                    const isPersonalProfile = /personal/i.test(profile.type);
                    const mayManageProfile = role !== 'contributor' || isMyProfile;
                    const canConnectDirectly = isMyProfile || !isPersonalProfile;
                    const connectReminderKey = `connect-${profile.id}`;
                    const invitationSentAt = state.reminders[connectReminderKey];
                    return (
                      <div className="profile-row" key={profile.id}>
                        <NetworkIcon network={profile.network} />
                        <div className="profile-details">
                          <strong>{profile.name}</strong>
                          <span>{translate(profile.type)}</span>
                          <span className={`profile-health ${profileHealthClass(profile.health)}`}>
                            <span className="health-dot" />
                            {translate(profile.health)}
                          </span>
                        </div>
                        {profile.health === 'Needs reconnecting' && mayManageProfile && (
                          <button
                            className="button small"
                            onClick={() => setConnectOpen(profile)}
                            {...readOnlyProps}
                          >
                            {translate('Reconnect')}
                          </button>
                        )}
                        {profile.health === 'Not connected' &&
                          mayManageProfile &&
                          (canConnectDirectly ? (
                            <button
                              className="button small"
                              onClick={() => setConnectOpen(profile)}
                              {...readOnlyProps}
                            >
                              {translate(
                                isMyProfile && isPersonalProfile ? 'Connect my profile' : 'Connect'
                              )}
                            </button>
                          ) : invitationSentAt ? (
                            <span className="status review">
                              {translate('Invitation sent {when}', { when: invitationSentAt })}
                            </span>
                          ) : (
                            <button
                              className="button small"
                              onClick={() => {
                                commands.remind(connectReminderKey);
                                notify(
                                  translate('Connect-only invitation sent to {name} (simulated)', {
                                    name: profile.owner,
                                  })
                                );
                              }}
                              {...readOnlyProps}
                            >
                              {translate('Invite to connect')}
                            </button>
                          ))}
                      </div>
                    );
                  })}
                  <button
                    className="profile-action"
                    onClick={() => setConnectOpen('new')}
                    disabled={readOnly || role === 'contributor'}
                    title={
                      role === 'contributor'
                        ? translate('Only {name} can connect accounts', { name: workspace.owner })
                        : undefined
                    }
                  >
                    <SafeIcon name="FiPlus" />
                    {translate('Connect a profile')}
                  </button>
                  <button className="profile-action" onClick={manageConnections}>
                    <SafeIcon name="FiSettings" />
                    {translate('Manage connections')}
                  </button>
                </div>
              )}
            </div>
            {lockedModules.map((module) => (
              <button
                className="module-locked"
                key={module.label}
                onClick={() => notify(translate('Coming soon — ask your agency'))}
              >
                <SafeIcon name={module.icon} />
                <span>{translate(module.label)}</span>
                <SafeIcon name="FiLock" size={14} />
              </button>
            ))}
          </>
        )}

        {isWorkspaceMember && (
          <div className="sidebar-bottom">
            <button className="nav-item" onClick={() => setContactOpen(true)} {...readOnlyProps}>
              <SafeIcon name="FiMessageCircle" />
              {labels.contact}
            </button>
          </div>
        )}
        <div className="mobile-sidebar-tools">
          <button
            className="button"
            aria-expanded={mobileNoticesOpen}
            onClick={() => setMobileNoticesOpen(!mobileNoticesOpen)}
          >
            <SafeIcon name="FiBell" />
            {translate('Notifications')}
            {unreadNotices.length > 0 && (
              <span className="notice-dot static">{unreadNotices.length}</span>
            )}
          </button>
          {mobileNoticesOpen && (
            <div className="mobile-notices">
              {notices.length === 0 && (
                <p className="muted">{translate('You are all caught up.')}</p>
              )}
              {[...notices].reverse().map((notice) => (
                <button
                  key={notice.id}
                  className={`popover-item ${notice.read ? '' : 'unread'}`}
                  onClick={() => {
                    const [linkedPage, id] = notice.link.split('/');
                    onClose();
                    navigate({ page: linkedPage as Page, id });
                  }}
                >
                  <span className="notice-marker" />
                  <span>
                    <strong>{translate(notice.text, notice.variables)}</strong>
                    <span className="muted">{timeLabel(notice.at, language, translate)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          <select
            className="select-field"
            value={language}
            onChange={(event) => setLanguage(event.target.value as Language)}
            aria-label={translate('Language')}
          >
            {SUPPORTED_LOCALES.map((locale) => (
              <option key={locale.code} value={locale.code}>
                {locale.flag} · {locale.name}
              </option>
            ))}
          </select>
          <div className="mobile-profile">
            <SafeIcon name="FiUser" />
            <span>
              <strong>{actor.name}</strong>
              <span className="muted">{workspace.name}</span>
            </span>
          </div>
        </div>
      </aside>

      {connectOpen && (
        <ConnectSheet
          account={connectOpen === 'new' ? undefined : connectOpen}
          onClose={() => setConnectOpen(null)}
        />
      )}

      {contactOpen && (
        <div className="profile-modal-backdrop" onClick={() => setContactOpen(false)}>
          <section
            className="profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="drawer-header">
              <h2 id="contact-title">{translate('Message Hakusana')}</h2>
              <button
                className="icon-button"
                onClick={() => setContactOpen(false)}
                aria-label={translate('Close')}
              >
                <SafeIcon name="FiX" />
              </button>
            </div>
            <p className="muted">
              {translate('Your agency team sees this together with {ws}.', { ws: workspace.name })}
            </p>
            <label htmlFor="contact-text">{translate('Your message')}</label>
            <textarea
              id="contact-text"
              className="text-field"
              rows={5}
              autoFocus
              value={contactText}
              onChange={(event) => setContactText(event.target.value)}
              placeholder={translate(
                'For example: can we plan posts for our Christmas opening hours?'
              )}
            />
            <div className="button-row" style={{ marginTop: 12 }}>
              <button
                className="button primary"
                disabled={!contactText.trim()}
                onClick={sendContact}
              >
                <SafeIcon name="FiSend" />
                {translate('Send message')}
              </button>
              <button className="button" onClick={() => setContactOpen(false)}>
                {translate('Close')}
              </button>
            </div>
            {myRequests.length > 0 && (
              <div className="contact-history">
                <h3>{translate('Your messages')}</h3>
                {[...myRequests].reverse().map((request) => (
                  <div key={request.id} className="checklist-item contact-item">
                    <div style={{ flex: 1 }}>
                      <div className="muted small-print">
                        {translate('You wrote at {time}', { time: request.at })}
                      </div>
                      <p>{request.text}</p>
                      {request.reply ? (
                        <div className="contact-reply">
                          <strong>
                            {translate('{name} from Hakusana replied at {time}', {
                              name: request.reply.by,
                              time: request.reply.at,
                            })}
                          </strong>
                          <p>{request.reply.text}</p>
                        </div>
                      ) : (
                        <span className="status review">{translate('Waiting for a reply')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
